import React from "react";
import { KTMScreen, ActionEvent, RenderEvent, renderScreen } from "ktm-ui";
import { KTMApi } from "api/KTMApi";
import { Layout, Dialog, Button, Column, Panel, Display, Card } from "ktm-ui";
import { CustomDropdown } from "components/CustomDropdown";
import { NationalDistributionPartnerBadge } from "components/NationalDistributionPartnerBadge";

interface IdsPolicy {
  Id: string;
  PrimaryAdvisor_Id: string;
  HasCorehealth: boolean;
  Representative: string;
}

const representatives = ["-- Select --", "NONE", "SALESON", "SALESAB", "JG"];

interface IdsAdvisor {
  Id: string;
  Name: string;
  Distro: string;
  IsNationalDistributionPartner: boolean;
}

export default KTMScreen(
  { name: "enroll.policy.change-advisor" },
  class extends React.Component<{ policy_id: string }> {
    data: {
      dsPolicy: IdsPolicy;
      dsAdvisor: IdsAdvisor;
    };

     // Store original data for comparison
     originalData: IdsAdvisor | null = null;
     originalRepresentative: string | null = null;

    state = {
      selectedRep: "NONE",
    };

    render() {
      return renderScreen(this);
    }

    async start({ screen }: ActionEvent) {
      screen.defaultRecord = "@dsPolicy";
      await KTMApi.start(screen, {
        "@policy_id": this.props.policy_id,
      });
      screen.title = "Edit Primary Advisor";
      // Store a copy of the original data
      this.originalData = { ...this.data.dsAdvisor };
      this.originalRepresentative = this.data.dsPolicy.Representative ?? null;
    }

    main({ field, draw, action }: RenderEvent) {
      return (
        <Layout>
          {draw(this.header)}
          {draw(this.main_tab)}
          {draw(this.footer)}
        </Layout>
      );
    }

    header({ field, draw, action }: RenderEvent) {
      return <Dialog.Header label="Edit Primary Advisor" />;
    }

    footer({ field, draw, action }: RenderEvent) {
      var isDisabled = this.data.dsPolicy.PrimaryAdvisor_Id === null ? true : false;

      return (
        <Dialog.Footer>
          <Button
            action={action(this.submit)}
            label="Save"
            size="large"
            style="primary"
            disable={isDisabled}
          />
        </Dialog.Footer>
      );
    }

    main_tab({ field, draw, action }: RenderEvent) {
      return (
        <Panel>
          <Column>{draw(this.details)}</Column>
          <Column></Column>
        </Panel>
      );
    }

    details({ field, draw, action }: RenderEvent) {
      return (
        <Card>
          <Display required field={field("PrimaryAdvisor_Id")} />
          <Display field={field("@dsAdvisor.Distro")} />
          <NationalDistributionPartnerBadge
            show={!!this.data.dsAdvisor?.IsNationalDistributionPartner}
          />
          <div className="RT-TextBox">
            <label>Representative</label>
            <CustomDropdown
              options={representatives}
              selectedOption={
                this.data.dsPolicy.Representative ?? this.state.selectedRep
              }
              setSelectedOption={this.setSelectedRep}
            />
          </div>
        </Card>
      );
    }

    setSelectedRep = async (selectedOption: any) => {
      const findOption = representatives.find((o) => o === selectedOption);
      this.setState({ selectedRep: findOption });
      this.data.dsPolicy.Representative = findOption;
    };

    getChangedFields(originalData, updatedData) {
      if (!originalData) return {};

      const changes = {};
      for (const key in updatedData) {
        if (!['Id', '$$meta'].includes(key) && updatedData[key] !== originalData[key]) {  // Exclude 'Id'
          changes[key] = {
              oldValue: originalData[key] !== undefined ? originalData[key] : "N/A",
              newValue: updatedData[key] !== undefined ? updatedData[key] : "N/A",
          };
        }
      }
      return changes;
    }

    async submit({ screen }: ActionEvent) {
      if (this.data.dsPolicy.HasCorehealth) {
        try {
          await screen.showWarning({
            message: "CH billing is enabled. Does the CH advisor also need updating?",
          });
        } catch {
          return;
        }
      }

      if (
        this.data.dsPolicy.Representative == undefined ||
        this.data.dsPolicy.Representative === "-- Select --" ||
        this.data.dsPolicy.Representative === "NONE"
      ) {
        this.data.dsPolicy.Representative = null;
      }

      // Track changes
      const changes = this.getChangedFields(
        this.originalData,
        this.data.dsAdvisor
      );

      if ((this.originalRepresentative ?? null) !== (this.data.dsPolicy.Representative ?? null)) {
        changes["Representative"] = {
          oldValue: this.originalRepresentative ?? "N/A",
          newValue: this.data.dsPolicy.Representative ?? "N/A",
        };
      }

      const logDetails = Object.entries(changes)
        .map(([key, value]) => {
          const change = value as { oldValue: any; newValue: any };
          return `${key}: ${change.oldValue} → ${change.newValue}`;
        })
        .join(" | ");

      const policyLogs = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Note: logDetails,
          Action: "Policy Update General settings",
          PolicyId: this.props.policy_id,
        }),
      };

      await KTMApi.action(screen, "submit", {
        "@dsPolicy.PrimaryAdvisor_Id": this.data.dsPolicy.PrimaryAdvisor_Id,
        "@dsPolicy.Representative": this.data.dsPolicy.Representative,
        "@policy_id": this.props.policy_id,
      }).then(() => {
        fetch("/api/PlanAdmin/updatepolicylogs", policyLogs);
      });
      screen.close({ continue: true });
    }
  }
);
